import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { tasksApi, customersApi } from '@/lib/api';
import { Button, Card, Field, NativeSelect } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { TextareaField, TextField } from '@/components/form/Felder';
import type { TaskCreate } from '@/lib/api';

const priorityOptions = [
  { value: 'low', label: 'Niedrig' },
  { value: 'medium', label: 'Mittel' },
  { value: 'high', label: 'Hoch' },
  { value: 'urgent', label: 'Dringend' },
];

interface FormData {
  title: string;
  description: string;
  due_date: string;
  priority: string;
}

export function TaskFormPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const customerId = searchParams.get('customer_id');
  const partnerId = searchParams.get('partner_id');

  const { data: customer } = useQuery({
    queryKey: ['customers', customerId],
    queryFn: () => customersApi.get(customerId!),
    enabled: !!customerId,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      title: '',
      description: '',
      due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      priority: 'medium',
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: TaskCreate) => tasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (customerId) {
        navigate(`/customers/${customerId}`);
      } else if (partnerId) {
        navigate(`/partners/${partnerId}`);
      } else {
        navigate('/tasks');
      }
    },
  });

  const onSubmit = (data: FormData) => {
    createMutation.mutate({
      ...data,
      priority: data.priority as TaskCreate['priority'],
      customer_id: customerId || undefined,
      partner_id: partnerId || undefined,
    });
  };

  const getBackUrl = () => {
    if (customerId) return `/customers/${customerId}`;
    if (partnerId) return `/partners/${partnerId}`;
    return '/tasks';
  };

  return (
    <div>
      <CrmPageHeader
        size="page"
        zurueck={{ to: getBackUrl(), label: 'Zurück' }}
        title="Aufgabe anlegen"
        subtitle={customer ? `Zu ${customer.company_name}` : undefined}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
        <Card title="Aufgabe" description="Details zur Aufgabe">
            <div className="space-y-5">
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Titel fehlt' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Titel"
                    required
                    error={errors.title?.message}
                    placeholder="Angebot nachfassen"
                  />
                )}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Controller
                  name="due_date"
                  control={control}
                  rules={{ required: 'Fälligkeit fehlt' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Fällig am"
                      type="date"
                      required
                      error={errors.due_date?.message}
                    />
                  )}
                />
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Field label="Priorität">{(f) => <NativeSelect {...f} {...field} options={priorityOptions} />}</Field>
                  )}
                />
              </div>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextareaField
                    {...field}
                    label="Beschreibung"
                    rows={4}
                    placeholder="Weitere Details zur Aufgabe"
                  />
                )}
              />
            </div>
        </Card>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={() => navigate(getBackUrl())} disabled={createMutation.isPending}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" loading={createMutation.isPending}>
            Aufgabe anlegen
          </Button>
        </div>
      </form>
    </div>
  );
}
